<template>
    <div class="app-container" style="width: 100%;">
      <!-- Sidebar -->
      <div class="sidebar">
        <div class="sidebar-header">
          <h1 class="sidebar-title">
            <Database class="sidebar-icon" />
            DB Admin
          </h1>
        </div>
        
        <!-- Database Selection -->
        <div class="database-section">
          <label class="form-label">База данных</label>
          <select 
            v-model="selectedDatabase" 
            @change="onDatabaseChange"
            class="form-select"
          >
            <option value="">Выберите БД</option>
            <option v-for="db in databases" :key="db" :value="db">{{ db }}</option>
          </select>
        </div>
  
        <!-- Tables List -->
        <div class="tables-section">
          <h3 class="tables-title">Таблицы</h3>
          <div class="tables-list">
            <button
              v-for="table in tables"
              :key="table"
              @click="selectTable(table)"
              :class="['table-button', { 'table-button-active': selectedTable === table }]"
            >
              <Table class="table-icon" />
              {{ table }}
            </button>
          </div>
        </div>
      </div>
  
      <!-- Main Content -->
      <div class="main-content">
        <!-- Header -->
        <div class="main-header">
          <div class="header-info">
            <h2 class="page-title">
              {{ selectedTable || 'Выберите таблицу' }}
            </h2>
            <p class="page-subtitle">
              {{ selectedDatabase ? `База данных: ${selectedDatabase}` : 'Управление данными' }}
            </p>
          </div>
          <button
            v-if="selectedTable"
            @click="showAddModal = true"
            class="add-button"
          >
            <Plus class="button-icon" />
            Добавить запись
          </button>
        </div>
  
        <!-- Table Content -->
        <div class="content-area">
          <div v-if="!selectedTable" class="empty-state">
            <Database class="empty-icon" />
            <h3 class="empty-title">Выберите таблицу</h3>
            <p class="empty-text">Выберите базу данных и таблицу для начала работы</p>
          </div>
  
          <div v-else class="data-container">
            <!-- Table Actions -->
            <div class="table-actions">
              <div class="actions-left">
                <div class="search-container">
                  <Search class="search-icon" />
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Поиск..."
                    class="search-input"
                  />
                </div>
                <button @click="refreshData" class="refresh-button">
                  <RefreshCw class="button-icon" />
                </button>
                <div class="page-size-selector">
                  <label class="page-size-label">Записей на странице:</label>
                  <select v-model="pageSize" class="page-size-select">
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
              <div class="records-count">
                <span v-if="totalRecords > 0">
                  Показано {{ startRecord }}-{{ endRecord }} из {{ totalRecords }} записей
                </span>
                <span v-else>
                  Записей не найдено
                </span>
                <span v-if="sortColumn" class="sort-info">
                  | Сортировка: {{ sortColumn }} ({{ sortOrder === 'asc' ? 'по возрастанию' : 'по убыванию' }})
                </span>
              </div>
            </div>
  
            <!-- Data Table -->
            <div class="table-wrapper">
              <div v-if="loading" class="loading-container">
                <div class="loading-spinner"></div>
                <p>Загрузка данных...</p>
              </div>
              
              <div v-else-if="error" class="error-container">
                <p class="error-message">{{ error }}</p>
                <button @click="loadTableData" class="retry-button">Повторить</button>
              </div>
              
              <table v-else class="data-table">
                <thead class="table-header">
                  <tr>
                    <th v-for="column in columns" :key="column" class="table-th sortable-th" @click="toggleSort(column)">
                      <div class="th-content">
                        <span>{{ column }}</span>
                        <span class="sort-icon">{{ getSortIcon(column) }}</span>
                      </div>
                    </th>
                    <th class="table-th table-th-actions">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody class="table-body">
                  <tr v-for="(record, index) in records" :key="index" class="table-row">
                    <td v-for="column in columns" :key="column" class="table-td">
                      {{ record[column] }}
                    </td>
                    <td class="table-td table-td-actions">
                      <button @click="editRecord(record, index)" class="action-button edit-button">
                        <Edit class="action-icon" />
                      </button>
                      <button @click="deleteRecord(index)" class="action-button delete-button">
                        <Trash2 class="action-icon" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
  
            <!-- Pagination -->
            <div v-if="totalRecords > 0" class="pagination">
              <div class="pagination-info">
                Страница {{ currentPage }} из {{ totalPages }}
              </div>
              <div class="pagination-controls">
                <!-- Первая страница -->
                <button
                  @click="goToFirstPage"
                  :disabled="currentPage === 1"
                  class="pagination-button"
                  title="Первая страница"
                >
                  <ChevronLeft class="pagination-icon" />
                  <ChevronLeft class="pagination-icon" style="margin-left: -8px;" />
                </button>
                
                <!-- Предыдущая страница -->
                <button
                  @click="goToPrevPage"
                  :disabled="currentPage === 1"
                  class="pagination-button"
                  title="Предыдущая страница"
                >
                  <ChevronLeft class="pagination-icon" />
                </button>
                
                <!-- Номера страниц -->
                <div class="page-numbers">
                  <button
                    v-for="page in pageNumbers"
                    :key="page"
                    @click="goToPage(page)"
                    :class="['page-number', { 'page-number-active': page === currentPage }]"
                  >
                    {{ page }}
                  </button>
                </div>
                
                <!-- Следующая страница -->
                <button
                  @click="goToNextPage"
                  :disabled="currentPage === totalPages"
                  class="pagination-button"
                  title="Следующая страница"
                >
                  <ChevronRight class="pagination-icon" />
                </button>
                
                <!-- Последняя страница -->
                <button
                  @click="goToLastPage"
                  :disabled="currentPage === totalPages"
                  class="pagination-button"
                  title="Последняя страница"
                >
                  <ChevronRight class="pagination-icon" />
                  <ChevronRight class="pagination-icon" style="margin-left: -8px;" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Add/Edit Modal -->
      <div v-if="showAddModal || showEditModal" class="modal-overlay">
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">
              {{ showAddModal ? 'Добавить запись' : 'Редактировать запись' }}
            </h3>
          </div>
          <div class="modal-body">
            <div v-for="column in columns" :key="column" class="form-group">
              <div class="form-label-container">
                <label class="form-label">{{ column }}</label>
                <template v-if="getFieldDescription(column)">
                  <div class="tooltip-wrapper">
                    <button 
                      type="button"
                      class="info-button"
                      :data-tooltip="getFieldDescription(column)"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                      </svg>
                    </button>
                  </div>
                </template>
              </div>
              <input
                v-model="formData[column]"
                type="text"
                class="form-input"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closeModal" class="button button-secondary">
              Отмена
            </button>
            <button @click="saveRecord" class="button button-primary">
              {{ showAddModal ? 'Добавить' : 'Сохранить' }}
            </button>
          </div>
        </div>
      </div>
  
      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="modal-overlay">
        <div class="modal modal-small">
          <div class="modal-body">
            <div class="delete-confirmation">
              <div class="delete-icon-container">
                <AlertTriangle class="delete-icon" />
              </div>
              <div class="delete-content">
                <h3 class="delete-title">Удалить запись</h3>
                <p class="delete-text">Это действие нельзя отменить</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="showDeleteModal = false" class="button button-secondary">
              Отмена
            </button>
            <button @click="confirmDelete" class="button button-danger">
              Удалить
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, watch, computed } from 'vue'
  import { 
    Database, 
    Table, 
    Plus, 
    Search, 
    RefreshCw, 
    Edit, 
    Trash2, 
    ChevronLeft, 
    ChevronRight,
    AlertTriangle
  } from 'lucide-vue-next'
  
  // Reactive data
  const selectedDatabase = ref('')
  const selectedTable = ref('')
  const searchQuery = ref('')
  const currentPage = ref(1)
  const pageSize = ref(10)
  const totalRecords = ref(0)
  const sortColumn = ref('id')
  const sortOrder = ref('asc')
  const showAddModal = ref(false)
  const showEditModal = ref(false)
  const showDeleteModal = ref(false)
  const editingIndex = ref(-1)
  const deleteIndex = ref(-1)
  
  // Reactive data
  const databases = ref([])
  const tables = ref([])
  const records = ref([])
  const columns = ref([])
  const formData = reactive({})
  const loading = ref(false)
  const error = ref('')

  // Функция для получения заголовков авторизации
  const getAuthHeaders = () => {
    const token = localStorage.getItem('userToken')
    if (!token) {
      throw new Error('Токен авторизации не найден. Пожалуйста, войдите в систему.')
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  }

  // Функция для обработки ошибок авторизации
  const handleAuthError = (err, response) => {
    if (response && response.status === 403) {
      error.value = 'Недостаточно прав доступа. Требуется роль администратора.'
      return true
    }
    if (response && response.status === 401) {
      error.value = 'Токен авторизации недействителен. Пожалуйста, войдите в систему заново.'
      return true
    }
    if (err.message && err.message.includes('Токен авторизации не найден')) {
      error.value = 'Токен авторизации не найден. Пожалуйста, войдите в систему.'
      return true
    }
    return false
  }
  

  

  
  // Methods
  const onDatabaseChange = async () => {
    if (selectedDatabase.value) {
      try {
        const response = await fetch(`https://ncht.bz/api/db/tables?db=${selectedDatabase.value}`, {
          headers: getAuthHeaders()
        })
        if (!response.ok) {
          if (handleAuthError(new Error(`HTTP error! status: ${response.status}`), response)) return
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        tables.value = data
      } catch (err) {
        console.error('Ошибка при получении таблиц:', err)
        if (!handleAuthError(err, null)) {
          error.value = 'Ошибка при получении таблиц'
        }
        tables.value = []
      }
    } else {
      tables.value = []
    }
    selectedTable.value = ''
    records.value = []
    columns.value = []
  }
  
  const selectTable = async (table) => {
    selectedTable.value = table
    currentPage.value = 1
    pageSize.value = 10
    
    try {
      // Получаем структуру таблицы
      const structureResponse = await fetch(`https://ncht.bz/api/db/table-structure?db=${selectedDatabase.value}&table=${table}`, {
        headers: getAuthHeaders()
      })
      if (!structureResponse.ok) {
        if (handleAuthError(new Error(`HTTP error! status: ${structureResponse.status}`), structureResponse)) return
        throw new Error(`HTTP error! status: ${structureResponse.status}`)
      }
      const structureData = await structureResponse.json()
      columns.value = structureData.columns.map(col => col.column_name)
      
      // Используем колонку для сортировки по умолчанию из API
      sortColumn.value = structureData.defaultSortColumn
      sortOrder.value = 'asc'
      
      // Получаем данные таблицы
      await loadTableData()
    } catch (err) {
      console.error('Ошибка при загрузке таблицы:', err)
      if (!handleAuthError(err, null)) {
        error.value = 'Ошибка при загрузке таблицы'
        columns.value = ['id', 'name', 'description', 'created_at']
        sortColumn.value = 'id'
        sortOrder.value = 'asc'
        records.value = []
      }
    }
    
    resetForm()
  }
  
  const resetForm = () => {
    Object.keys(formData).forEach(key => delete formData[key])
    columns.value.forEach(column => {
      formData[column] = ''
    })
  }
  
  const editRecord = (record, index) => {
    editingIndex.value = index
    Object.keys(formData).forEach(key => delete formData[key])
    Object.assign(formData, { ...record })
    showEditModal.value = true
  }
  
  const deleteRecord = (index) => {
    deleteIndex.value = index
    showDeleteModal.value = true
  }
  
  const confirmDelete = async () => {
    try {
      const recordToDelete = records.value[deleteIndex.value]
      const response = await fetch(`https://ncht.bz/api/db/delete-data?db=${selectedDatabase.value}&table=${selectedTable.value}&id=${recordToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      })
      
      if (!response.ok) {
        if (handleAuthError(new Error(`HTTP error! status: ${response.status}`), response)) return
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const result = await response.json()
      if (result.success) {
        await loadTableData() // Перезагружаем данные
      }
      
      showDeleteModal.value = false
      deleteIndex.value = -1
    } catch (err) {
      console.error('Ошибка при удалении записи:', err)
      if (!handleAuthError(err, null)) {
        error.value = 'Ошибка при удалении записи'
      }
    }
  }
  
  const saveRecord = async () => {
    try {
      if (showAddModal.value) {
        // Добавление новой записи
        const response = await fetch('https://ncht.bz/api/db/write-data', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            db: selectedDatabase.value,
            table: selectedTable.value,
            data: formData
          })
        })
        
        if (!response.ok) {
          if (handleAuthError(new Error(`HTTP error! status: ${response.status}`), response)) return
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const result = await response.json()
        if (result.success) {
          await loadTableData() // Перезагружаем данные
        }
      } else if (showEditModal.value) {
        // Обновление записи
        const response = await fetch('https://ncht.bz/api/db/update-data', {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            db: selectedDatabase.value,
            table: selectedTable.value,
            id: formData.id,
            data: formData
          })
        })
        
        if (!response.ok) {
          if (handleAuthError(new Error(`HTTP error! status: ${response.status}`), response)) return
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const result = await response.json()
        if (result.success) {
          await loadTableData() // Перезагружаем данные
        }
      }
      closeModal()
    } catch (err) {
      console.error('Ошибка при сохранении записи:', err)
      if (!handleAuthError(err, null)) {
        error.value = 'Ошибка при сохранении записи'
      }
    }
  }
  
  const closeModal = () => {
    showAddModal.value = false
    showEditModal.value = false
    editingIndex.value = -1
    resetForm()
  }
  
  const loadTableData = async () => {
    if (!selectedDatabase.value || !selectedTable.value) return
    
    loading.value = true
    error.value = ''
    
    try {
      const limit = pageSize.value
      const offset = (currentPage.value - 1) * pageSize.value
      const search = searchQuery.value
      
      let url = `https://ncht.bz/api/db/read-data?db=${selectedDatabase.value}&table=${selectedTable.value}&limit=${limit}&offset=${offset}&sort=${sortColumn.value}&order=${sortOrder.value}`
      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      })
      if (!response.ok) {
        if (handleAuthError(new Error(`HTTP error! status: ${response.status}`), response)) return
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      records.value = data
      
      // Получаем общее количество записей
      await loadTotalCount()
    } catch (err) {
      console.error('Ошибка при загрузке данных:', err)
      if (!handleAuthError(err, null)) {
        error.value = 'Ошибка при загрузке данных'
      }
      records.value = []
    } finally {
      loading.value = false
    }
  }

  const loadTotalCount = async () => {
    if (!selectedDatabase.value || !selectedTable.value) return
    
    try {
      const search = searchQuery.value
      let url = `https://ncht.bz/api/db/count?db=${selectedDatabase.value}&table=${selectedTable.value}`
      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      })
      if (!response.ok) {
        if (handleAuthError(new Error(`HTTP error! status: ${response.status}`), response)) return
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      totalRecords.value = data.total
    } catch (err) {
      console.error('Ошибка при получении общего количества:', err)
      handleAuthError(err, null)
      totalRecords.value = 0
    }
  }

  const refreshData = async () => {
    await loadTableData()
  }

  const toggleSort = (column) => {
    if (sortColumn.value === column) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortColumn.value = column
      sortOrder.value = 'asc'
    }
  }

  const getSortIcon = (column) => {
    if (sortColumn.value !== column) return '↕️'
    return sortOrder.value === 'asc' ? '⬆️' : '⬇️'
  }

  // Вычисляемые свойства для пагинации
  const totalPages = computed(() => Math.ceil(totalRecords.value / pageSize.value))
  
  const startRecord = computed(() => (currentPage.value - 1) * pageSize.value + 1)
  
  const endRecord = computed(() => Math.min(currentPage.value * pageSize.value, totalRecords.value))
  
  const pageNumbers = computed(() => {
    const pages = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage.value - Math.floor(maxVisiblePages / 2))
    let endPage = Math.min(totalPages.value, startPage + maxVisiblePages - 1)
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    return pages
  })

  // Функции навигации
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page
    }
  }

  const goToFirstPage = () => goToPage(1)
  const goToLastPage = () => goToPage(totalPages.value)
  const goToPrevPage = () => goToPage(currentPage.value - 1)
  const goToNextPage = () => goToPage(currentPage.value + 1)
  
  // Инициализация при загрузке компонента
  const initializeDatabases = async () => {
    try {
      const response = await fetch('https://ncht.bz/api/db/databases', {
        headers: getAuthHeaders()
      })
      if (!response.ok) {
        if (handleAuthError(new Error(`HTTP error! status: ${response.status}`), response)) return
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      databases.value = data
    } catch (err) {
      console.error('Ошибка при получении списка баз данных:', err)
      if (!handleAuthError(err, null)) {
        error.value = 'Ошибка при получении списка баз данных'
      }
      databases.value = []
    }
  }

  // Обработка поиска
  watch(searchQuery, async () => {
    if (selectedTable.value) {
      currentPage.value = 1
      await loadTableData()
    }
  })

  // Обработка изменения страницы
  watch(currentPage, async () => {
    if (selectedTable.value) {
      await loadTableData()
    }
  })

  // Обработка изменения сортировки
  watch([sortColumn, sortOrder], async () => {
    if (selectedTable.value) {
      currentPage.value = 1
      await loadTableData()
    }
  })

  // Обработка изменения размера страницы
  watch(pageSize, async () => {
    if (selectedTable.value) {
      currentPage.value = 1
      await loadTableData()
    }
  })

  watch(selectedTable, () => {
    resetForm()
  })

  // Описания полей для таблицы origins
  const fieldDescriptions = {
    'platform': 'platform – платформа источника трафика (например, Google, Яндекс, Facebook, VK и т.д.)',
    'origin': 'origin – источник трафика, например: Запуск, Фокус, Рассылки, Активы Нечто, Другое.',
    'event': 'event – событие или тип события, связанное с источником трафика',
    'lead': 'lead – к кому относить лиды(Теплый/ Холодный трафик)',
    'utm_source': 'utm_source – определяет источник трафика, например, название социальной сети, рекламной платформы или сайта, откуда пришел пользователь',
    'utm_medium': 'utm_medium – указывает на канал продвижения. Для свободного ввода указать .edit',
    'utm_campaign': 'utm_campaign – обозначает цель рекламной кампании. Для выбора из списка указать .utm_campaign',
    'utm_content': 'utm_content – помогает различать варианты рекламных материалов в одной кампании.',
    'utm_term': 'utm_term – служит для дополнительной детализации, фиксирует дату запуска рекламного объявления для удобного анализа'
  }

  // Получить описание поля (только для таблицы origins)
  const getFieldDescription = (column) => {
    if (selectedTable.value === 'origins') {
      return fieldDescriptions[column] || null
    }
    return null
  }


  // Инициализация при загрузке
  initializeDatabases()
  </script>
  
  <style scoped>
  /* Reset and base styles */
  * {
    box-sizing: border-box;
  }
  
  /* Main layout */
  .app-container {
    display: flex;
    height: 100%;
    min-height: 600px;
    background-color: #f9fafb;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  
  /* Sidebar */
  .sidebar {
    width: 256px;
    background-color: white;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    border-right: 1px solid #e5e7eb;
    flex-shrink: 0;
  }
  
  .sidebar-header {
    padding: 24px;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .sidebar-title {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
  }
  
  .sidebar-icon {
    width: 24px;
    height: 24px;
    color: #2563eb;
  }
  
  .database-section {
    padding: 16px;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .tables-section {
    padding: 16px;
  }
  
  .tables-title {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin: 0 0 12px 0;
  }
  
  .tables-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  .table-button {
    width: 100%;
    text-align: left;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    border: none;
    background: none;
    color: #4b5563;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .table-button:hover {
    background-color: #f3f4f6;
  }
  
  .table-button-active {
    background-color: #dbeafe;
    color: #1d4ed8;
    font-weight: 500;
  }
  
  .table-icon {
    width: 16px;
    height: 16px;
  }
  
  /* Main content */
  .main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  
  .main-header {
    background-color: white;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid #e5e7eb;
    padding: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .page-title {
    font-size: 32px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }
  
  .page-subtitle {
    color: #4b5563;
    margin: 4px 0 0 0;
  }
  
  .add-button {
    background-color: #2563eb;
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background-color 0.2s;
    font-size: 14px;
  }
  
  .add-button:hover {
    background-color: #1d4ed8;
  }
  
  .button-icon {
    width: 16px;
    height: 16px;
  }
  
  /* Content area */
  .content-area {
    flex: 1;
    padding: 24px;
    overflow: hidden;
  }
  
  .empty-state {
    text-align: center;
    padding: 48px 0;
  }
  
  .empty-icon {
    width: 64px;
    height: 64px;
    color: #9ca3af;
    margin: 0 auto 16px;
  }
  
  .empty-title {
    font-size: 18px;
    font-weight: 500;
    color: #111827;
    margin: 0 0 8px 0;
  }
  
  .empty-text {
    color: #4b5563;
    margin: 0;
  }
  
  /* Data container */
  .data-container {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .table-actions {
    padding: 16px 24px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  
  .actions-left {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
  }
  
  .search-container {
    position: relative;
  }
  
  .search-icon {
    width: 16px;
    height: 16px;
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
  }
  
  .search-input {
    padding: 8px 16px 8px 40px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    width: 200px;
  }
  
  .search-input:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  .refresh-button {
    padding: 8px;
    color: #4b5563;
    border: none;
    background: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .refresh-button:hover {
    color: #1f2937;
    background-color: #f3f4f6;
  }
  
  .records-count {
    font-size: 14px;
    color: #4b5563;
    text-align: end;
  }

  .sort-info {
    color: #2563eb;
    font-weight: 500;
  }

  .page-size-selector {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-right: 20px;
    flex-shrink: 0;
  }

  .page-size-label {
    font-size: 14px;
    color: #4b5563;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .page-size-select {
    padding: 4px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 14px;
    background-color: white;
    width: auto;
    min-width: 60px;
    flex-shrink: 0;
  }
  
  /* Table */
  .table-wrapper {
    overflow-x: auto;
    flex: 1;
    overflow-y: auto;
  }
  
  .data-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .table-header {
    background-color: #f9fafb;
  }
  
  .table-th {
    padding: 12px 24px;
    text-align: left;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .sortable-th {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s;
  }

  .sortable-th:hover {
    background-color: #f3f4f6;
  }

  .th-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .sort-icon {
    font-size: 14px;
    opacity: 0.7;
  }
  
  .table-th-actions {
    text-align: right;
  }
  
  .table-body {
    background-color: white;
  }
  
  .table-row {
    border-top: 1px solid #e5e7eb;
    transition: background-color 0.2s;
  }
  
  .table-row:hover {
    background-color: #f9fafb;
  }
  
  .table-td {
    padding: 16px 24px;
    white-space: nowrap;
    font-size: 14px;
    color: #111827;
  }
  
  .table-td-actions {
    text-align: right;
  }
  
  .action-button {
    padding: 4px;
    border: none;
    background: none;
    border-radius: 4px;
    cursor: pointer;
    margin-left: 12px;
    transition: all 0.2s;
  }
  
  .edit-button {
    color: #2563eb;
  }
  
  .edit-button:hover {
    color: #1d4ed8;
    background-color: #dbeafe;
  }
  
  .delete-button {
    color: #dc2626;
  }
  
  .delete-button:hover {
    color: #b91c1c;
    background-color: #fee2e2;
  }
  
  .action-icon {
    width: 16px;
    height: 16px;
  }
  
  /* Pagination */
  .pagination {
    padding: 16px 24px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .pagination-info {
    font-size: 14px;
    color: #4b5563;
  }
  
  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .page-numbers {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .page-number {
    padding: 4px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 14px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 32px;
    text-align: center;
  }

  .page-number:hover {
    background-color: #f3f4f6;
  }

  .page-number-active {
    background-color: #2563eb;
    color: white;
    border-color: #2563eb;
  }

  .page-number-active:hover {
    background-color: #1d4ed8;
  }
  
  .pagination-button {
    padding: 4px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    background-color: white;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .pagination-button:hover:not(:disabled) {
    background-color: #f9fafb;
  }
  
  .pagination-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .pagination-current {
    padding: 4px 12px;
    font-size: 14px;
  }
  
  .pagination-icon {
    width: 16px;
    height: 16px;
  }
  
  /* Form elements */
  .form-label-container {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  .form-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin: 0;
  }

  .info-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: none;
    color: #6b7280;
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s;
    flex-shrink: 0;
  }

  .info-button:hover {
    color: #2563eb;
    background-color: #eff6ff;
  }

  /* Tooltip styles */
  .tooltip-wrapper {
    position: relative;
    display: inline-block;
  }

  .tooltip-wrapper .info-button[data-tooltip]:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 8px;
    padding: 10px 14px;
    background-color: #1f2937;
    color: white;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.5;
    white-space: normal;
    width: 320px;
    max-width: 90vw;
    z-index: 1000;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    pointer-events: none;
    text-align: left;
    word-wrap: break-word;
    font-weight: normal;
  }

  .tooltip-wrapper .info-button[data-tooltip]:hover::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 2px;
    border: 6px solid transparent;
    border-top-color: #1f2937;
    z-index: 1001;
    pointer-events: none;
  }
  
  .form-select,
  .form-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
  }
  
  .form-select:focus,
  .form-input:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  /* Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
  }
  
  .modal {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    width: 100%;
    max-width: 448px;
    margin: 16px;
  }
  
  .modal-small {
    max-width: 384px;
  }
  
  .modal-header {
    padding: 16px 24px;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .modal-title {
    font-size: 18px;
    font-weight: 500;
    color: #111827;
    margin: 0;
  }
  
  .modal-body {
    padding: 16px 24px;
  }
  
  .form-group {
    margin-bottom: 16px;
  }
  
  .modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
  
  /* Buttons */
  .button {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
  }
  
  .button-primary {
    background-color: #2563eb;
    color: white;
  }
  
  .button-primary:hover {
    background-color: #1d4ed8;
  }
  
  .button-secondary {
    background-color: white;
    color: #374151;
    border: 1px solid #d1d5db;
  }
  
  .button-secondary:hover {
    background-color: #f9fafb;
  }
  
  .button-danger {
    background-color: #dc2626;
    color: white;
  }
  
  .button-danger:hover {
    background-color: #b91c1c;
  }
  
  /* Delete confirmation */
  .delete-confirmation {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }
  
  .delete-icon-container {
    width: 40px;
    height: 40px;
    background-color: #fee2e2;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  
  .delete-icon {
    width: 20px;
    height: 20px;
    color: #dc2626;
  }
  
  .delete-title {
    font-size: 18px;
    font-weight: 500;
    color: #111827;
    margin: 0 0 4px 0;
  }
  
  .delete-text {
    font-size: 14px;
    color: #4b5563;
    margin: 0;
  }

  /* Loading and error states */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px;
    color: #6b7280;
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #2563eb;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px;
    color: #dc2626;
  }

  .error-message {
    margin: 0 0 16px 0;
    font-size: 14px;
  }

  .retry-button {
    background-color: #dc2626;
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
  }

  .retry-button:hover {
    background-color: #b91c1c;
  }
  </style>